import { apiClient, apiErrorMessage, fetcher } from './apiClient';
import { makeThumbnail } from '../utils/makeThumbnail';

export type Asset = {
    id: string;
    orgId: string;
    name: string;
    /** Absolute public URL — this is what a component's `url` is set to. */
    url: string;
    /** Client-generated at upload time; null is normal. */
    thumbnailUrl: string | null;
    mimeType: string;
    sizeBytes: number;
    originalFilename: string;
    width: number | null;
    height: number | null;
    componentsCount?: number;
    createdAt?: string;
    updatedAt?: string;
};

/**
 * PNG only for now. Widening this needs the matching `mimetypes:` rule in
 * AssetController::store — the server is what actually enforces it.
 */
export const ACCEPTED_ASSET_MIME_TYPES = ['image/png'];

/**
 * Pre-flight limit, mirroring ASSET_MAX_MB. This exists to fail fast rather than
 * make someone wait out a doomed 60 MB upload; the server remains authoritative,
 * and its 422 names the real limit if the two ever drift.
 */
export const ASSET_MAX_MB = 50;
export const ASSET_MAX_BYTES = ASSET_MAX_MB * 1024 * 1024;

/** Human-readable size for the library tiles. */
export const formatAssetSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Why an upload was rejected before it left the browser, phrased for display.
 * Returns null when the file is acceptable.
 */
export const validateAssetFile = (file: File): string | null => {
    if (!ACCEPTED_ASSET_MIME_TYPES.includes(file.type)) {
        return `${file.name} is not a PNG.`;
    }
    if (file.size > ASSET_MAX_BYTES) {
        return `${file.name} is ${formatAssetSize(file.size)}. The limit is ${ASSET_MAX_MB} MB.`;
    }
    return null;
};

export const listAssets = () => fetcher<Asset[]>('api/assets');
export const getAsset = (id: string) => fetcher<Asset>(`api/assets/${id}`);

/**
 * Uploads a file and, when the browser managed to produce one, its thumbnail.
 *
 * Note the deliberate absence of a Content-Type header: the browser has to set
 * it so the multipart boundary is included. Setting it by hand produces a body
 * the server cannot parse.
 */
export const uploadAsset = async (
    file: File,
    csrfToken: string | null | undefined,
    options: { name?: string } = {},
): Promise<Asset> => {
    const rejection = validateAssetFile(file);
    if (rejection) throw new Error(rejection);

    const formData = new FormData();
    formData.append('file', file);
    if (options.name) formData.append('name', options.name);

    const thumbnail = await makeThumbnail(file);
    if (thumbnail) formData.append('thumbnail', thumbnail);

    try {
        const res = await apiClient.post('api/assets', formData, {
            headers: { 'X-XSRF-TOKEN': csrfToken || '' },
        });
        return JSON.parse(res.data) as Asset;
    } catch (err) {
        throw new Error(apiErrorMessage(err, `Could not upload ${file.name}.`));
    }
};

export const renameAsset = async (
    id: string,
    name: string,
    csrfToken: string | null | undefined,
): Promise<Asset> => {
    const res = await apiClient.put(`api/assets/${id}`, JSON.stringify({ name }), {
        headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': csrfToken || '' },
    });
    return JSON.parse(res.data) as Asset;
};

/**
 * Thrown when an asset is still in use. Carries the components pointing at it so
 * the UI can name them before asking for confirmation.
 */
export class AssetInUseError extends Error {
    constructor(
        message: string,
        readonly components: Array<{ id: string; name: string }>,
    ) {
        super(message);
        this.name = 'AssetInUseError';
    }
}

/**
 * Deletes an asset. Without `force` the server refuses with a 409 when
 * components still point at it, which surfaces here as AssetInUseError.
 */
export const deleteAsset = async (
    id: string,
    csrfToken: string | null | undefined,
    options: { force?: boolean } = {},
): Promise<void> => {
    const query = options.force ? '?force=1' : '';

    try {
        await apiClient.delete(`api/assets/${id}${query}`, {
            headers: { 'X-XSRF-TOKEN': csrfToken || '' },
        });
    } catch (err: any) {
        if (err?.response?.status === 409) {
            const body = parseBody(err.response.data);
            throw new AssetInUseError(
                body?.message ?? 'This image is still in use.',
                body?.components ?? [],
            );
        }
        throw new Error(apiErrorMessage(err, 'Could not delete this image.'));
    }
};

/** apiClient is a raw Axios instance, so response bodies arrive as strings. */
const parseBody = (data: unknown): any => {
    if (typeof data !== 'string') return data;
    try {
        return JSON.parse(data);
    } catch {
        return null;
    }
};
